import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { NonConformityStationModel } from '../../models/non-conformity-station';
import { NonConformityStationAdapter } from '../../adapters/non-conformity-station';
import { NonConformityTypeModel } from '../../models/non-conformity-type';
import { NonConformityTypeAdapter } from '../../adapters/non-conformity-type';
import { NonConformityModel } from '../../models/non-conformity';
import { NonConformityAdapter } from '../../adapters/non-conformity';
import { PaginatedResponse } from '../../../../core/services/api/paginated-response';

const BASE_URL = environment.apiBaseUrl + '/non-conformities';
const STATIONS_URL = environment.apiBaseUrl + '/weather-station/stations';

@Injectable({
  providedIn: 'root',
})
export class NonConformitiesApiService {
  constructor(private readonly http: HttpClient) {}

  getUnresolvedCount(): Observable<number> {
    const headers = new HttpHeaders().set('X-Skip-Loading', 'true');

    return this.http
      .get<any>(`${BASE_URL}/unresolved-count`, { headers })
      .pipe(map((response) => response?.data?.total ?? 0));
  }

  postResolve(ids: number[]): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/resolve`, ids);
  }

  postUnresolve(ids: number[]): Observable<any> {
    return this.http.post<any>(`${BASE_URL}/unresolve`, ids);
  }

  getStations(): Observable<NonConformityStationModel[]> {
    return this.http
      .get<any>(STATIONS_URL)
      .pipe(
        map((response: any) =>
          response.data.map(NonConformityStationAdapter.fromApi),
        ),
      );
  }

  getTypes(): Observable<NonConformityTypeModel[]> {
    return this.http
      .get<any>(`${BASE_URL}/types`)
      .pipe(
        map((response: any) =>
          response.data.map(NonConformityTypeAdapter.fromApi),
        ),
      );
  }

  getPaginated(
    page: number,
    size: number,
    sortField: string,
    sortDirection: string,
    filters?: {
      stationIds?: string;
      pluviometerIds?: string;
      types?: string;
      resolved?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Observable<PaginatedResponse<NonConformityModel>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', `${sortField},${sortDirection}`);

    if (filters?.stationIds)
      params = params.set('stationIds', filters.stationIds);
    if (filters?.pluviometerIds)
      params = params.set('pluviometerIds', filters.pluviometerIds);
    if (filters?.types) params = params.set('types', filters.types);
    if (filters?.resolved !== undefined && filters.resolved !== '')
      params = params.set('resolved', filters.resolved);
    if (filters?.startDate) params = params.set('startDate', filters.startDate);
    if (filters?.endDate) params = params.set('endDate', filters.endDate);

    return this.http.get<any>(`${BASE_URL}/paginated`, { params }).pipe(
      map((response) => {
        const data = response.data;
        return {
          content: (data.content || []).map(NonConformityAdapter.fromApi),
          totalElements: data.totalElements || 0,
          totalPages: data.totalPages || 0,
          size: data.size || size,
          number: data.currentPage ?? data.number ?? page,
          hasNext: data.hasNext || false,
          hasPrevious: data.hasPrevious || false,
        };
      }),
    );
  }
}
